import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Plus, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { extractGarmentSuffix } from '@/lib/displayUtils';

const priorities = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const AddGarmentDialog = ({ 
  open, 
  onOpenChange, 
  orderId,
  orderNumber,
  lineItemsWithRefs,
  existingGarments,
  factories = [],
  onGarmentCreated,
  apiUrl,
  token,
  initialGarmentToRemake // New prop to pre-select garment for remake
}) => {
  const [activeTab, setActiveTab] = useState('new');
  const [loading, setLoading] = useState(false);
  
  // New Garment state
  const [selectedLineItem, setSelectedLineItem] = useState('');
  const [priority, setPriority] = useState('normal');
  const [patternNo, setPatternNo] = useState('');
  const [productionFactoryId, setProductionFactoryId] = useState('__none__');
  
  // Remake Garment state
  const [selectedGarment, setSelectedGarment] = useState('');

  const getBaseRemakeGarmentNo = (garmentNo = '') => {
    const match = String(garmentNo).match(/^(.*?)(?:-RE\d+)$/i);
    return match ? match[1] : garmentNo;
  };

  const getNextRemakeSuffix = (garment) => {
    if (!garment?.garment_no) return 'Re1';
    const baseGarmentNo = getBaseRemakeGarmentNo(garment.garment_no);
    const remakeCount = existingGarments.filter((existingGarment) => {
      const existingBase = getBaseRemakeGarmentNo(existingGarment.garment_no || '');
      return existingBase === baseGarmentNo;
    }).length - 1;

    return `Re${Math.max(1, remakeCount + 1)}`;
  };

  // When dialog opens with a garment to remake, switch to remake tab and pre-select it
  useEffect(() => {
    if (open && initialGarmentToRemake) {
      setActiveTab('remake');
      setSelectedGarment(initialGarmentToRemake.garment_id);
    }
  }, [open, initialGarmentToRemake]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setActiveTab('new');
      setSelectedLineItem('');
      setSelectedGarment('');
      setPriority('normal');
      setPatternNo('');
      setProductionFactoryId('__none__');
    }
  }, [open]);

  useEffect(() => {
    if (!selectedLineItem) return;
    const lineItem = lineItemsWithRefs.find((item) => item.line_item_id === selectedLineItem);
    setProductionFactoryId(lineItem?.production_factory_id || '__none__');
  }, [selectedLineItem, lineItemsWithRefs]);

  // Get next garment number for a line item
  const getNextGarmentNumber = (lineItem) => {
    if (!lineItem) return '';
    
    const lineItemRef = lineItem.line_item_ref;
    const existingForLineItem = existingGarments.filter(g => 
      g.line_item_id === lineItem.line_item_id
    );
    
    const nextNumber = existingForLineItem.length + 1;
    return `${orderNumber}-${lineItemRef}${nextNumber}`;
  };

  const handleCreateNewGarment = async () => {
    if (!selectedLineItem) {
      toast.error('Please select a line item');
      return;
    }

    setLoading(true);
    try {
      const lineItem = lineItemsWithRefs.find(li => li.line_item_id === selectedLineItem);
      
      const garmentData = {
        order_id: orderId,
        line_item_id: selectedLineItem,
        garment_type: lineItem.garment_type,
        priority: priority,
        pattern_no: patternNo || null,
        production_factory_id: productionFactoryId === '__none__' ? null : productionFactoryId,
      };

      const response = await fetch(`${apiUrl}/api/garments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(garmentData)
      });

      if (!response.ok) throw new Error('Failed to create garment');

      const newGarment = await response.json();
      
      toast.success(`Garment ${newGarment.garment_no} created successfully`);
      onGarmentCreated(newGarment);
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating garment:', error);
      toast.error('Failed to create garment');
    } finally {
      setLoading(false);
    }
  };

  const handleRemakeGarment = async () => {
    if (!selectedGarment) {
      toast.error('Please select a garment to remake');
      return;
    }

    setLoading(true);
    try {
      const originalGarment = existingGarments.find(g => g.garment_id === selectedGarment);
      const remakeSuffix = getNextRemakeSuffix(originalGarment);
      
      const garmentData = {
        order_id: orderId,
        line_item_id: originalGarment.line_item_id,
        garment_type: originalGarment.garment_type,
        priority: originalGarment.priority,
        pattern_no: null,
        production_factory_id: originalGarment.production_factory_id || null,
        is_test_garment: originalGarment.is_test_garment || false, // Inherit from original
        custom_suffix: remakeSuffix,
      };

      const response = await fetch(`${apiUrl}/api/garments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(garmentData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create remake garment');
      }

      const newGarment = await response.json();
      
      const fabricNote = originalGarment.is_test_garment 
        ? '(No fabric orders auto-created for test garment)' 
        : '(Fabric orders auto-created based on original customizations)';
      
      toast.success(`Remake created as ${newGarment.garment_no}! ${fabricNote}`, {
        duration: 5000,
      });
      
      onGarmentCreated(newGarment);
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating remake garment:', error);
      toast.error(error.message || 'Failed to create remake garment');
    } finally {
      setLoading(false);
    }
  };

  const selectedLineItemData = lineItemsWithRefs.find(li => li.line_item_id === selectedLineItem);
  const selectedGarmentData = existingGarments.find(g => g.garment_id === selectedGarment);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle data-testid="add-garment-dialog-title">Add Garment</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="add-garment-dialog-tabs">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="new" data-testid="add-garment-new-tab-trigger">
              <Plus className="w-4 h-4 mr-2" />
              New Garment
            </TabsTrigger>
            <TabsTrigger value="remake" data-testid="add-garment-remake-tab-trigger">
              <Copy className="w-4 h-4 mr-2" />
              Remake Garment
            </TabsTrigger>
          </TabsList>

          {/* New Garment Tab */}
          <TabsContent value="new" className="space-y-4 mt-4">
            <div className="space-y-4">
              <div>
                <Label>Select Line Item *</Label>
                <Select value={selectedLineItem} onValueChange={setSelectedLineItem}>
                  <SelectTrigger className="mt-1" data-testid="add-garment-line-item-select-trigger">
                    <SelectValue placeholder="Choose a line item..." />
                  </SelectTrigger>
                  <SelectContent data-testid="add-garment-line-item-select-content">
                    {lineItemsWithRefs.map((item) => (
                      <SelectItem key={item.line_item_id} value={item.line_item_id} data-testid={`add-garment-line-item-option-${item.line_item_id}`}>
                        {item.line_item_ref} - {item.product_name} ({item.garment_type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedLineItemData && (
                  <p className="text-xs text-slate-500 mt-1">
                    New garment will be: <span className="font-mono font-semibold">{getNextGarmentNumber(selectedLineItemData)}</span>
                  </p>
                )}
              </div>

              <div>
                <Label>Priority</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger className="mt-1" data-testid="add-garment-priority-select-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent data-testid="add-garment-priority-select-content">
                    {priorities.map((p) => (
                      <SelectItem key={p.value} value={p.value} data-testid={`add-garment-priority-option-${p.value}`}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Production Factory (Optional)</Label>
                <Select value={productionFactoryId} onValueChange={setProductionFactoryId}>
                  <SelectTrigger className="mt-1" data-testid="add-garment-factory-select-trigger">
                    <SelectValue placeholder="Select factory" />
                  </SelectTrigger>
                  <SelectContent data-testid="add-garment-factory-select-content">
                    <SelectItem value="__none__" data-testid="add-garment-factory-option-none">No factory</SelectItem>
                    {factories.map((factory) => (
                      <SelectItem
                        key={factory.factory_id}
                        value={factory.factory_id}
                        data-testid={`add-garment-factory-option-${factory.factory_id}`}
                      >
                        {factory.factory_name} ({factory.factory_code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Pattern Number (Optional)</Label>
                <Input
                  value={patternNo}
                  onChange={(e) => setPatternNo(e.target.value)}
                  placeholder="Enter pattern number..."
                  className="mt-1"
                  data-testid="add-garment-pattern-number-input"
                />
              </div>
            </div>
          </TabsContent>

          {/* Remake Garment Tab */}
          <TabsContent value="remake" className="space-y-4 mt-4">
            <div className="space-y-4">
              <div>
                <Label>Select Garment to Remake *</Label>
                <Select value={selectedGarment} onValueChange={setSelectedGarment}>
                  <SelectTrigger className="mt-1" data-testid="add-garment-remake-select-trigger">
                    <SelectValue placeholder="Choose a garment..." />
                  </SelectTrigger>
                  <SelectContent data-testid="add-garment-remake-select-content">
                    {existingGarments.map((garment) => {
                      const lineItem = lineItemsWithRefs.find(li => li.line_item_id === garment.line_item_id);
                      return (
                        <SelectItem key={garment.garment_id} value={garment.garment_id} data-testid={`add-garment-remake-option-${garment.garment_id}`}>
                          {garment.garment_no} - {lineItem?.product_name || garment.garment_type}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {selectedGarmentData && (
                  <div className="mt-2 p-3 bg-slate-50 rounded-md">
                    <p className="text-xs text-slate-700">
                      Remake will be created as <span className="font-mono font-semibold">{extractGarmentSuffix(`${getBaseRemakeGarmentNo(selectedGarmentData.garment_no)}-${getNextRemakeSuffix(selectedGarmentData)}`, orderNumber)}</span>
                    </p>
                    <p className="text-xs text-slate-500 italic">
                      Design customizations will be inherited from the original garment. Add any remake remarks in the garment notes column after creation.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading} data-testid="add-garment-cancel-button">
            Cancel
          </Button>
          <Button 
            onClick={activeTab === 'new' ? handleCreateNewGarment : handleRemakeGarment}
            disabled={loading || (activeTab === 'new' ? !selectedLineItem : !selectedGarment)}
            data-testid="add-garment-confirm-button"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {activeTab === 'new' ? 'Create Garment' : 'Create Remake'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddGarmentDialog;
